import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { AbstractDataService, LoggerService } from '@pf/shared-services';
import {
    ItemCategoriesService,
    ItemCustomFieldsService,
    ItemsService,
} from '@control-tower/platform-item-catalog';
import {
    CsvOrExcelOpenXmlMime,
    EntityChangeTrackingDto,
    IChangesSearchParams,
    ICustomField,
    IPagedResult,
    IPagedSearchParams,
    PFItemCatalogEntities,
    spreadsheetExportAcceptHeader,
} from '@pf/shared-common';
import { ItemStore } from './item.store';
import {
    CategoryViewModelDto,
    ItemCreate,
    ItemRead,
    ItemSearchParams,
} from '../../entities/item.dto';
import { Operation } from 'fast-json-patch';

const itemSearchIncludes = 'WithFiles';

@Injectable()
export class ItemDataService extends AbstractDataService<ItemRead, ItemCreate> {
    private service = inject(ItemsService);
    private itemCategoryService = inject(ItemCategoriesService);
    private customFieldsService = inject(ItemCustomFieldsService);

    protected EntityName = PFItemCatalogEntities.Item;

    constructor() {
        const store = inject(ItemStore);
        const logger = inject(LoggerService);

        super(store, logger);
    }

    protected createObs$(body: ItemCreate): Observable<ItemRead> {
        return this.service.itemEntityCreate({
            itemViewModelBody: body,
        }) as Observable<ItemRead>;
    }

    protected deleteObs$(id: string): Observable<ItemRead> {
        return this.service.itemEntityDelete({
            id,
        }) as Observable<ItemRead>;
    }

    protected undoDeleteObs$(id: string): Observable<ItemRead> {
        return this.service.itemEntityUndoDelete({
            id,
        }) as Observable<ItemRead>;
    }

    protected getObs$(id: string): Observable<ItemRead> {
        return this.service.itemEntityReadById({
            id,
        }) as Observable<ItemRead>;
    }

    protected updateObs$(
        id: string,
        operations: Operation[]
    ): Observable<ItemRead> {
        return this.service.itemEntityUpdatePatch({
            id,
            operation: operations,
        }) as Observable<ItemRead>;
    }

    protected searchObs$(
        searchParams: ItemSearchParams
    ): Observable<IPagedResult<ItemRead>> {
        const request: ItemSearchParams = {
            ...searchParams,
            include: itemSearchIncludes,
        };
        return this.service.itemEntitySearch(request).pipe(
            map((res): IPagedResult<ItemRead> => {
                const paged = res as IPagedResult<ItemRead>;
                return {
                    ...paged,
                    data: paged.data ?? [],
                };
            })
        );
    }

    override searchAll$(
        queryParams: ItemSearchParams,
        addToPage?: boolean,
        limit?: number
    ): Observable<ItemRead[]> {
        const itemSearchParams = { ...queryParams } as ItemSearchParams;
        if (itemSearchParams.isDeleted === undefined) {
            itemSearchParams.isDeleted = false;
        }
        return super.searchAll$(
            itemSearchParams as IPagedSearchParams,
            addToPage,
            limit
        );
    }

    protected searchChangesObs$(
        searchParams: IChangesSearchParams
    ): Observable<IPagedResult<EntityChangeTrackingDto<ItemRead>>> {
        return this.service.itemEntityGetChanges(searchParams) as Observable<
            IPagedResult<EntityChangeTrackingDto<ItemRead>>
        >;
    }

    addItemCategories$(
        itemId: string,
        categoriesToAdd: CategoryViewModelDto[]
    ) {
        return categoriesToAdd.map(category => {
            return this.itemCategoryService.itemCategoryCreate({
                itemId,
                categoryId: category.id,
            });
        });
    }

    deleteItemCategories$(
        itemId: string,
        categoriesToDelete: CategoryViewModelDto[]
    ) {
        return categoriesToDelete.map(category => {
            return this.itemCategoryService.itemCategoryDelete({
                itemId,
                categoryId: category.id,
            });
        });
    }

    protected override createCustomFieldObs$(
        entityId: string,
        customField: ICustomField
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.itemEntityCreateCustomField({
            entityId,
            customFieldViewModelCreate: {
                key: customField.key,
                value: customField.value,
            },
        }) as Observable<ICustomField & { id: string }>;
    }

    protected override updateCustomFieldObs$(
        entityId: string,
        customFieldId: string,
        customField: ICustomField
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.itemEntityCustomFieldsUpdatePut({
            entityId,
            id: customFieldId,
            customFieldViewModelCreate: {
                key: customField.key,
                value: customField.value,
            },
        }) as Observable<ICustomField & { id: string }>;
    }

    protected override deleteCustomFieldObs$(
        entityId: string,
        customFieldId: string
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.itemEntityDeleteCustomField({
            entityId,
            id: customFieldId,
        }) as Observable<ICustomField & { id: string }>;
    }

    protected override exportObs$(
        params: ItemSearchParams & {
            exportType: string;
            filename: string;
            pageNumber: number;
            pageSize: number;
            acceptHeader?: string;
        }
    ): Observable<Blob> {
        const { exportType, filename, acceptHeader, ...searchRest } = params;
        const accept =
            acceptHeader || spreadsheetExportAcceptHeader(exportType);
        return this.service.itemEntityExport(
            {
                ...(searchRest as ItemSearchParams),
                exportType,
                filename,
            },
            'body',
            false,
            {
                httpHeaderAccept: accept as CsvOrExcelOpenXmlMime,
            }
        );
    }

    exportServer$(
        tableQuery: ItemSearchParams,
        exportType: string,
        filename: string,
        acceptHeader?: string
    ): Observable<Blob> {
        return this.exportObs$({
            ...tableQuery,
            exportType,
            filename,
            pageNumber: tableQuery.pageNumber ?? 1,
            pageSize: tableQuery.pageSize ?? 10_000,
            acceptHeader,
        });
    }
}
